// 11 §11.1 SettingsStore / PlayerSettings (§16 `player_settings`, §14 accessibility).
import { create } from 'zustand';
import { desktop } from '../desktop';

/** Encounter lighting (docs/19): full atmosphere, a gentler version, or flat. */
export type LightingPref = 'full' | 'soft' | 'off';

export type ColourMode = 'default' | 'deuteranopia' | 'protanopia' | 'tritanopia' | 'highContrast';

/**
 * Levels 0–100 (docs/34): master over everything; `sfx` is the game-wide sound-effects level (the
 * interface, combat, voices); `ambience` the weather and the land; `music` the score (docs/44).
 */
export interface AudioSettings {
  master: number; sfx: number; ambience: number; music: number;
  /** Silence without losing the levels. */
  muted: boolean;
  /** Quiet while the window or tab is in the background. */
  muteInBackground: boolean;
  /** Retro chimes (docs/44): an 8-bit flourish in place of level-ups, item claims and loot. */
  retro: boolean;
}

export interface PlayerSettings {
  uiScaleBp: number; colourMode: ColourMode;
  hideUnselectedPaths: boolean; effectIntensityBp: number; reduceMotion: boolean; highlightDanger: boolean;
  textSizeBp: number; zoomPref: 2 | 3 | 4 | 'fit'; playbackSpeed: 1 | 2 | 4; audio: AudioSettings;
  controller: Record<string, string>; telemetryOptIn: boolean;
  lighting: LightingPref;
  /** Storms flash lightning (docs/20 W5); off keeps the storm without the flashes. */
  lightningFlashes: boolean;
  /** Small creatures in the encounter (docs/35): butterflies, mice, gnats. Decoration only. */
  wildlife: boolean;
  /** docs/42 CM5: resting the mouse at the window's edge pans the encounter camera. */
  edgePan: boolean;
  /** docs/87: rebound hotkeys, by hotkey id (`core/input/hotkeys.ts`); an id not here keeps its defaults. */
  hotkeys: Record<string, string[]>;
}

/** The local copy kept through the desktop bridge (`userData/settings.json`) or localStorage: the settings and the store's revision. */
export interface LocalSettings extends PlayerSettings { updatedAt: number }

export interface SettingsStore extends PlayerSettings {
  hydrateFrom(server: PlayerSettings | null): void;
  save(): void;
  set(patch: Partial<PlayerSettings>): void;
  updatedAt: number;
}

export const DEFAULT_SETTINGS: PlayerSettings = {
  uiScaleBp: 10000, colourMode: 'default',
  hideUnselectedPaths: false, effectIntensityBp: 10000, reduceMotion: false, highlightDanger: true,
  textSizeBp: 10000, zoomPref: 'fit', playbackSpeed: 1, audio: { master: 80, sfx: 80, ambience: 70, music: 60, muted: false, muteInBackground: true, retro: false },
  controller: {}, telemetryOptIn: false, lighting: 'full', lightningFlashes: true, wildlife: true, edgePan: true,
  hotkeys: {},
};

const pick = (s: SettingsStore): PlayerSettings => ({
  uiScaleBp: s.uiScaleBp, colourMode: s.colourMode, hideUnselectedPaths: s.hideUnselectedPaths, effectIntensityBp: s.effectIntensityBp,
  reduceMotion: s.reduceMotion, highlightDanger: s.highlightDanger, textSizeBp: s.textSizeBp, zoomPref: s.zoomPref, playbackSpeed: s.playbackSpeed,
  audio: s.audio, controller: s.controller, telemetryOptIn: s.telemetryOptIn, lighting: s.lighting, lightningFlashes: s.lightningFlashes, wildlife: s.wildlife, edgePan: s.edgePan,
  hotkeys: s.hotkeys,
});

/**
 * The stored copy (JSON text through the bridge, docs/11 §13.2) as an object: anything unreadable or not
 * an object is no local copy, and only known keys are kept, so an older or hand-edited file cannot
 * put a stray key or a bad `audio` / `updatedAt` into the store.
 */
export function asLocal(stored: unknown): Partial<LocalSettings> | null {
  let v = stored;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return null; } }
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  const out: Record<string, unknown> = {};
  for (const k of [...Object.keys(DEFAULT_SETTINGS), 'updatedAt']) if (k in v) out[k] = (v as Record<string, unknown>)[k];
  const audio = out.audio;
  if (audio !== undefined && (typeof audio !== 'object' || audio === null || Array.isArray(audio))) delete out.audio;
  if (out.updatedAt !== undefined && !Number.isFinite(out.updatedAt)) delete out.updatedAt;
  return out as Partial<LocalSettings>;
}

export const useSettings = create<SettingsStore>()((set, get) => ({
  ...DEFAULT_SETTINGS,
  updatedAt: 0,
  hydrateFrom(server) {
    // Newer `updated_at` wins; local wins while offline (§14). Local copy comes through the desktop bridge or localStorage.
    desktop().settings.read().then((stored) => {
      const local = asLocal(stored);
      const chosen = server && (!local?.updatedAt || (server as { updatedAt?: number }).updatedAt! > local.updatedAt) ? server : local;
      if (chosen) set({ ...DEFAULT_SETTINGS, ...chosen, audio: { ...DEFAULT_SETTINGS.audio, ...chosen.audio } });
    }).catch((e: unknown) => console.warn('settings: could not read the local copy', e));
  },
  save() {
    const now = get().updatedAt;
    desktop().settings.write(JSON.stringify({ ...pick(get()), updatedAt: now }))
      .catch((e: unknown) => console.warn('settings: could not save the local copy', e));
    // Real: reducer `save_player_settings` (§16).
  },
  set(patch) { set({ ...patch, updatedAt: get().updatedAt + 1 }); get().save(); },
}));
